<template>
  <div>
    <TableList
      v-loading="listLoading"
      :dataList="list"
      :columnAllData="columnAllData"
      :tableName="TableName"
      :permissionName="permissionName"
      :isShowPagination="false"
      :canCommand="false"
      @command-change="handleCommand"
      :canRefresh="false"
      :canChangeColumn="false"
      :highlightCurrentRow="true"
    >
      <template #createUser="{ row }">
        <span class="user-name-tag">{{ formatUser(row) }}</span>
      </template>
    </TableList>
    <el-pagination
      class="mt-20"
      v-show="total > 0"
      :total="total"
      v-model:currentPage="listQuery.page"
      :page-size="listQuery.size"
      :page-sizes="pageSizes"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      layout="total, sizes, prev, pager, next"
    />
  </div>
</template>

<script lang="ts">
import { useStore } from '@/store'
import { defineComponent, reactive, toRefs, ref, onMounted } from 'vue'
import { userOperationLogList } from '@/apis/UserLogs'
import { getDropdown, getPageList } from '@/apis/Dictionary'
import { getDropdownByType } from '@/apis/tool'

import TableList from '@/components/common/TableList.vue'
import { TableName, permissionName } from '@/utils/enum'
import { TableField } from './FieldTable'
import { pageVo, pageSizes } from '@/utils/index'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'Operationlog',
  components: {
    TableList
  },
  setup() {
    const { t } = useI18n()
    const store = useStore()
    const userId = store.state.user.userId
    const userMap = ref<Record<number, { name: string; username: string }>>({})

    const dataMap = reactive({
      TableName: TableName.TABLE_USER_OPERATIONLOG,
      permissionName: permissionName.USER_OPERATIONLOG,
      columnAllData: TableField,

      activeId: 0,
      list: [],
      total: 0,
      listLoading: false,
      listQuery: {
        ...pageVo
      },
      pageSizes,
      dialogFormVisible: false,
      submitLoading: false,

      handleCommand(command: string, val?: any) {
        switch (command) {
          case 'refresh':
            dataMap.handleFilter()
            break
          case 'column':
            break
          case 'current-change':
            dataMap.handleCurrentChange(val)
            break
          case 'size-change':
            dataMap.handleSizeChange(val)
            break
        }
      },
      getList() {
        dataMap.listLoading = true
        userOperationLogList(dataMap.listQuery)
          .then((res: any) => {
            dataMap.list = res.data || []
            dataMap.total = res.total
            dataMap.listLoading = false
          })
          .catch(() => {
            dataMap.listLoading = false
          })
      },
      handleReset() {
        dataMap.listQuery = {
          ...pageVo
        }
        dataMap.handleFilter()
      },
      handleFilter() {
        dataMap.getList()
      },
      handleCurrentChange(page?: any) {
        dataMap.listQuery.page = page
        dataMap.getList()
      },
      handleSizeChange(size: any) {
        dataMap.listQuery.size = size
        dataMap.handleCurrentChange(1)
      },
      loadDropdownList(url: string) {
        getDropdown(url).then((res: any) => {
          dataMap[`${url}Option`] = res
        })
      },
      getDropdownByType(url: string) {
        getDropdownByType('ReviewStatus').then((res: any) => {
          dataMap[`${url}Option`] = res
        })
      },
      loadTableSelectOption() {
        dataMap.columnAllData.forEach((column: any) => {
          if (column.selectType === 1 && column.url) {
            getDropdownByType(column.url).then((res: any) => {
              column.option = res
                ? res.map((k: any) => {
                  return {
                    ...k,
                    name: k.mark
                  }
                })
                : []
            })
          }
        })
      }
    })

    // 加载用户字典用于将 createUser(ID) 映射为真实姓名
    async function loadUserMap() {
      try {
        const res: any = await getPageList('user', { page: 1, size: 200 })
        const map: Record<number, { name: string; username: string }> = {}
        if (res && res.data) {
          res.data.forEach((u: any) => {
            map[u.id] = {
              name: u.name,
              username: u.username
            }
          })
        }
        userMap.value = map
      } catch (err) {
        console.warn('获取用户映射字典失败:', err)
      }
    }

    // 多级优雅降级格式化用户展示
    function formatUser(row: any) {
      const uid = Number(row.createUser)
      const user = userMap.value[uid]
      if (user && user.name) {
        if (user.username && user.username !== user.name) {
          return `${user.name} (${user.username})`
        }
        return user.name
      }
      // 优雅降级：若未匹配到系统有效用户，优先显示日志自带的 username，最后兜底显示 ID
      if (row.username) {
        return `${row.username}${row.createUser ? ` (ID: ${row.createUser})` : ''}`
      }
      return row.createUser ? `用户 ID: ${row.createUser}` : '-'
    }

    onMounted(async () => {
      await loadUserMap()
      await dataMap.loadTableSelectOption()
      dataMap.getList()
    })

    return {
      t,
      ...toRefs(dataMap),
      userId,
      userMap,
      formatUser
    }
  }
})
</script>

<style lang="scss" scoped>
.user-name-tag {
  font-weight: 500;
  color: var(--ink);
}
</style>
